import AsyncStorage from '@react-native-async-storage/async-storage';
import { Coordinate } from 'types/map';
import { NotificationType } from 'types/notification';

const intervalTime = 600000; // 10 minutes

function pad(n) {
  return n < 10 ? '0' + n : n;
}

export type DBType = {
  id: string;
  title: string;
  description: string;
  level: number;
  latlng?: Coordinate;
  timestamp: EpochTimeStamp;
  occurrence?: string;
  occur?: string;
};

export async function getNotifications() {
  return fetch(`${process.env.SERVER_URL}/notifications`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  })
    .then((res) => res.json())
    .then((res: DBType[]) => {
      return res.map(
        ({ id, title, description, timestamp, level, occurrence, occur }) => ({
          id,
          title,
          description,
          timestamp,
          level,
          occurrence,
          occur,
        }),
      );
    })
    .catch((err) => {
      console.log('Fetch notifications error', err);
      return null;
    });
}

async function getData(): Promise<NotificationType[] | null> {
  const prev = await getSeen();
  const lastSeen = prev
    ? prev.filter((p) => p !== null).map((p) => p.id)
    : null;
  const date = new Date();
  const startTime = `${date.getFullYear().toString()}-${pad(
    date.getMonth() + 1,
  )}-${pad(date.getDate())} 0:00:00`;
  return fetch(
    `${process.env.SERVER_URL}/notifications?lastSeen=${lastSeen}&startTime=${startTime}`,
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  )
    .then((res) => res.json())
    .then((res: DBType[]) => {
      if (!res.length) {
        throw 'No notifications';
      }

      return res.map(
        ({ id, title, description, timestamp, level, occurrence, occur }) => ({
          id,
          title,
          description,
          timestamp,
          level,
          occurrence,
          occur,
        }),
      );
    })
    .catch((err) => {
      console.log('Fetch notifications error', err);
      return null;
    });
}

export async function watchNotifications(callback) {
  const data = await getData();
  callback(data);

  setInterval(async () => {
    const newData = await getData();
    callback(newData);
  }, intervalTime);
}

export async function getSeen() {
  const prev = await AsyncStorage.getItem('notifications');
  try {
    const json = JSON.parse(prev);
    if (json) {
      return json;
    }

    return null;
  } catch (err) {
    return null;
  }
}

export async function saveSeen(notifications: NotificationType[]) {
  try {
    const prev = await getSeen();
    if (prev) {
      await AsyncStorage.setItem(
        'notifications',
        JSON.stringify(notifications.concat(prev)),
      );
    } else {
      await AsyncStorage.setItem(
        'notifications',
        JSON.stringify(notifications),
      );
    }
  } catch (error) {
    // Error saving data
    console.log('Save notifications error', error);
  }
}

export async function addNotification(notification: NotificationType) {
  return fetch(`${process.env.SERVER_URL}/notifications`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(notification),
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.status && res.status !== 200) {
        throw 'Add notification failed';
      }
    })
    .catch((err) => {
      console.log('Add notification error', err);
      throw err;
    });
}

export async function changeNotification(notification: NotificationType) {
  const { id } = notification;
  return fetch(`${process.env.SERVER_URL}/notifications/${id}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(notification),
  }).then((res) => {
    if (res.status !== 200) {
      throw 'Change notification failed';
    }
  });
}

export async function deleteNotification(id: string) {
  return fetch(`${process.env.SERVER_URL}/notifications/${id}`, {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id }),
  })
    .then((res) => res.json())
    .then((res) => {
      if (res.status && res.status !== 200) {
        throw 'Delete notification failed';
      }
    })
    .catch((err) => {
      console.log('Delete notification error', err);
      throw err;
    });
}
